import algoliasearch from 'algoliasearch';
import turfDistance from '@turf/distance';
import { LanguageSupportUtil } from '../../../../../utils/language_support_util';
import { PageStrings } from '../../../../../utils/page_strings_util';
import { StringConstantsMixin } from '../shared/string_constants_mixin';
import { ServerCommunicationUtil } from '../../../../../utils/server_communication_util';
import { SearchResultsUtil } from '../../../../../utils/search_results_util';
import { SearchFiltersUtil } from '../../../../../utils/search_filters_util';

const ET_PHOTO_USER_SLUG = 'alltrails-user';

// TODO: Refactor this into algolia helpers/service and separate state management from Algolia requests
const SearchAppAlgoliaMixin = {
  initAlgolia() {
    this.algoliaRef = algoliasearch('9IOACG5NHE', '63a3cf94e0042b9c67abf0892fc1d223');
    this.algoliaIndex = this.algoliaRef.initIndex(StringConstantsMixin.constants.ALGOLIA.INDEX);
  },
  performSearch() {
    // Sanity check that our index is ready to go!
    // Dont search on trail map page, or if quick-draw is enabled
    if (
      !this.algoliaIndex ||
      this.state.page === PageStrings.EXPLORE_TRAIL_MAP_PAGE ||
      this.state.page === PageStrings.EXPLORE_USERS_TRACKS_MAP_PAGE ||
      this.state.page === PageStrings.EXPLORE_USERS_MAPS_MAP_PAGE ||
      this.state.page === PageStrings.EXPLORE_PENDING_PAGE
    ) {
      return;
    }

    if ([PageStrings.EXPLORE_USERS_TRACKS_PAGE, PageStrings.EXPLORE_USERS_MAPS_PAGE].includes(this.state.page)) {
      if (this.props.initialUserSlug == ET_PHOTO_USER_SLUG || !this.state.atMaps) {
        return;
      }
      // search through / filter user's recordings or maps
      this.setState({
        results: SearchFiltersUtil.customSearch(this.state.atMaps, this.state.filters, this.props.context, this.state.isMobileWidth),
        loading: false
      });
      return;
    }

    // search for recordings or trails
    const my = this.state.page !== PageStrings.EXPLORE_COMPLETED_PAGE;
    const completedIds = Object.keys(this.itemsInListOfType(my, this.completedMagicId, 'trail'));
    const verifiedIds = Object.keys(this.itemsInListOfType(my, this.verifiedMagicId, 'trail'));
    const algoliaQueryObject = SearchFiltersUtil.createAlgoliaQueryObject({
      filters: this.state.filters,
      mobileWidth: this.state.isMobileWidth,
      page: this.state.page,
      completedIds,
      verifiedIds
    });
    this.algoliaIndex
      .search(this.state.filters.queryTerm || '', algoliaQueryObject)
      .then(this.handleSearchResults)
      .catch(err => console.error(err));

    // search for places (for admin filter)
    if (this.props.context.adminUser) {
      const algoliaAreaQueryObject = SearchFiltersUtil.createAlgoliaLocQueryObject(
        ['area', 'place'],
        this.state.filters,
        this.state.isMobileWidth,
        this.state.page
      );
      this.algoliaIndex
        .search('', algoliaAreaQueryObject)
        .then(this.handlePlaceSearchResults)
        .catch(err => console.error(err));
    }
  },
  handleSearchResults(content) {
    const center = this.refs.searchMap.getMapCenter();
    const bounds = this.refs.searchMap.getMapBounds();
    // Bounds 0,2 is Top Left corner
    const searchRadius = turfDistance([center[1], center[0]], [bounds[2], bounds[0]]) / 2;
    const resultsWithLocations = content.hits.filter(result => {
      return !(result._geoloc.lat == null || result._geoloc.lng == null || (result._geoloc.lat == 0 && result._geoloc.lng == 0));
    });
    // Sort results and zoom to fit
    const getListItem = (type, ID) => this.getListItemInList(false, this.state.listId, type, ID);
    const sortedResults = SearchResultsUtil.applySortFunction(resultsWithLocations, this.state.filters, center, searchRadius, getListItem);
    if (this.zoomToFitInitLocationObj(sortedResults)) {
      return;
    }
    // remove area filters and refresh if no trails show up in area
    if (sortedResults && sortedResults.length < 1 && this.state.filters.areas && Object.keys(this.state.filters.areas).length > 0) {
      this.state.filters.areas = {};
      this.setState({ results: null, filters: this.state.filters, loading: false }, this.replaceHistoryStateAndSearch);
      return;
    }
    this.setState({ results: sortedResults, loading: false });
  },
  zoomToFitInitLocationObj(sortedResults) {
    if (
      sortedResults &&
      sortedResults.length &&
      this.state.filters.initLocationObject &&
      this.state.filters.initLocationObject.objectID &&
      !this.state.results
    ) {
      // Split location objectID into parts
      const { initLocationObject } = this.state.filters;
      const splt = initLocationObject.objectID.split('-');
      const initLocationType = splt[0];
      const initLocationId = parseInt(splt[1]);
      // Compare new proposed zoom with max zoom for location type
      const maxZoom = this.refs.searchMap.getMaxZoom(initLocationType);
      const newZoom = this.refs.searchMap.getMapZoom() + 1;
      if (newZoom > maxZoom) {
        // Do not zoom-in any further than max zoom for location type
        return false;
      }
      // Count how many of top few results are in the initial location
      let numResultsInLoc = 0;
      const numResultsTotal = Math.min(5, sortedResults.length);
      sortedResults.slice(0, 5).forEach(location => {
        if (
          (initLocationType == 'area' && location.area_id == initLocationId) ||
          (initLocationType == 'cityo' && location.city_id == initLocationId) ||
          (initLocationType == 'state' && location.state_id == initLocationId) ||
          (initLocationType == 'country' && location.country_id == initLocationId)
        ) {
          numResultsInLoc++;
        }
      });
      // If a majority of the points returned are not in the selected location
      if (numResultsInLoc < numResultsTotal / 2) {
        const center = initLocationObject._geoloc;
        if (center) {
          // Zoom-in to center of location and return
          this.refs.searchMap.setMapCenterAndZoom(center.lat, center.lng, newZoom);
          return true;
        }
        console.log('Location object does not have _geoloc:', initLocationObject);
      }
    }
    return false;
  },
  filterResults(results, objectIdPrefix) {
    return results.filter(r => {
      return r.objectID.indexOf(objectIdPrefix) > -1;
    });
  },
  handlePlaceSearchResults(content) {
    const newFilterState = this.state.filters;

    let needToPerformSearch = false;
    [
      ['areas', 'area-'],
      ['countries', 'country-'],
      ['states', 'state-'],
      ['cities', 'cityo-']
    ].forEach(keys => {
      const results = this.filterResults(content.hits, keys[1]);
      if (this.handleLocSearchResults(newFilterState, keys[0], results)) {
        needToPerformSearch = true;
      }
    });

    this.setState({ filters: newFilterState }, needToPerformSearch ? this.replaceHistoryStateAndSearch : null);
  },
  handleLocSearchResults(filters, filtersKey, results) {
    // Use loc results to modify locs filter

    const limit = 10;
    const locs = filters[filtersKey];

    // Sort results by selected, popularity, name, then finally id
    results = results
      .sort((a, b) => {
        const aSelected = locs[a.ID] && locs[a.ID].selected;
        const bSelected = locs[b.ID] && locs[b.ID].selected;
        if (aSelected && !bSelected) {
          return -1;
        }
        if (!aSelected && bSelected) {
          return 1;
        }
        if (a.popularity !== b.popularity) {
          return b.popularity - a.popularity;
        }
        if (a.name) {
          return a.name.localeCompare(b.name);
        }
        return b.ID - a.ID;
      })
      .slice(0, limit);

    // Handle results accordingly
    const locsToAdd = [];
    results.forEach(result => {
      const currLoc = locs[result.ID];
      const name = LanguageSupportUtil.findRecordFieldWithLocale(result, 'name', this.props.context.languageRegionCode);
      if (currLoc && currLoc.selected) {
        // update currently selected
        currLoc.name = name;
        currLoc.popularity = result.popularity;
      } else {
        // new
        locsToAdd.push({
          value: result.ID,
          name,
          popularity: result.popularity,
          selected: false
        });
      }
    });

    // Creating clone so we can assign new values all at once at bottom of func (prevents weird concurrency issues)
    const newLocs = { ...locs };

    // Remove locs that aren't selected
    for (const key in newLocs) {
      if (newLocs.hasOwnProperty(key) && !newLocs[key].selected) {
        delete newLocs[key];
      }
    }

    // Add to newLocs until reaches total count of 10
    const count = Object.keys(newLocs).length;
    for (let i = 0; i < locsToAdd.length; i++) {
      const loc = locsToAdd[i];
      if (count >= limit) {
        break;
      }
      newLocs[loc.value] = loc;
    }

    // Unselect location and perform a new search if Algolia results never came back with a name
    // (likely that the location query param was set for a location that doesn't exist in the bounding box)
    let needToPerformSearch = false;
    for (const key in newLocs) {
      if (newLocs.hasOwnProperty(key) && !newLocs[key].name) {
        needToPerformSearch = true;
        break;
      }
    }

    filters[filtersKey] = newLocs;

    return needToPerformSearch;
  },
  getAlgoliaTrail(trailId) {
    if (!trailId) return;
    this.algoliaIndex
      .search('', {
        hitsPerPage: 1,
        distinct: 1,
        getRankingInfo: 1,
        allowTyposOnNumericTokens: 0,
        facets: ['type'],
        facetFilters: ['type:trail'],
        numericFilters: [`ID=${trailId}`]
      })
      .then(this.handleTrailSearchResult)
      .catch(err => console.error(err));
  },
  handleTrailSearchResult(content) {
    if (content.hits && content.hits.length < 1) {
      return;
    }
    const trail = content.hits[0];
    if (this.state.page === PageStrings.EXPLORE_TRAIL_MAP_PAGE) {
      this.setState({ selectedObject: trail });
    } else if (
      [PageStrings.EXPLORE_USERS_MAPS_MAP_PAGE, PageStrings.EXPLORE_USERS_TRACKS_MAP_PAGE, PageStrings.EXPLORE_LIFELINE_PAGE].includes(
        this.state.page
      )
    ) {
      this.setState({ trackTrail: trail });
    }
  },
  handleTrailLocationClick(locationString, locationType) {
    const handleGetObject = content => {
      this.handleLocationSearchResult({ hits: [content] });
    };
    switch (locationType) {
      case 'state':
      case 'city':
        this.algoliaIndex
          .search(locationString, {
            hitsPerPage: 1,
            distinct: 1,
            getRankingInfo: 1,
            allowTyposOnNumericTokens: 0,
            facets: ['type'],
            facetFilters: ['type:place']
          })
          .then(this.handleLocationSearchResult)
          .catch(err => console.error(err));
        break;
      case 'area':
        this.algoliaIndex
          .search('', {
            hitsPerPage: 1,
            distinct: 1,
            getRankingInfo: 1,
            allowTyposOnNumericTokens: 0,
            facets: ['type'],
            facetFilters: ['type:area'],
            numericFilters: [
              `ID=${locationString}` // Use exact park ID for area search
            ]
          })
          .then(this.handleLocationSearchResult)
          .catch(err => console.error(err));
        break;
      case 'state_id':
        this.algoliaIndex.getObject(`state-${locationString}`).then(handleGetObject);
        break;
      case 'city_id':
        this.algoliaIndex.getObject(`cityo-${locationString}`).then(handleGetObject);
        break;
      default:
        return null;
    }
  },
  handleLocationSearchResult(content) {
    if (content.hits && content.hits.length > 0) {
      if (this.state.page === PageStrings.EXPLORE_USERS_TRACKS_MAP_PAGE || this.state.page === PageStrings.EXPLORE_USERS_MAPS_MAP_PAGE) {
        const url = `/${content.hits[0].slug}`;
        window.open(url, '_blank');
      } else {
        this.handleObjectSelected(content.hits[0]);
      }
    }
  },
  requiresBoundsCalc(obj) {
    function isType(t) {
      return obj.objectID.indexOf(t) >= 0;
    }
    return isType('area') || isType('city') || isType('state') || isType('country');
  },
  getBoundingBoxOfTrails(obj, success, error) {
    const splt = obj.objectID.split('-');
    const [sType, id] = splt;
    $.ajax({
      url: '/api/alltrails/locations/bounding_box',
      type: 'get',
      data: {
        s_type: sType,
        id
      },
      headers: {
        'X-AT-KEY': ServerCommunicationUtil.apiKey
      },
      success,
      error
    });
  },
  handleObjectSelected(obj) {
    // make changes to filter state
    const newFilterState = this.state.filters;
    this.resetPlacesFilterState(newFilterState, obj);
    SearchFiltersUtil.setFilter(newFilterState, 'locationObject', JSON.parse(JSON.stringify(obj)));
    SearchFiltersUtil.setFilter(newFilterState, 'initLocationObject', JSON.parse(JSON.stringify(obj)));
    SearchFiltersUtil.setFilter(newFilterState, 'queryTerm', null);
    // handle differently if requires bounding box of trails in location
    if (this.requiresBoundsCalc(obj)) {
      this.getBoundingBoxOfTrails(
        obj,
        // success
        data => {
          if (data) {
            // update using bounding box of all trails in location
            newFilterState.boundingBox = data;
            newFilterState.zoomLevel = null;
            const locationType = obj.objectID.split('-')[0];
            this.refs.searchMap.setMapBounds(data, { locationType });
            const titleString = this.state.page === PageStrings.EXPLORE_ALL_PAGE ? `Map of ${obj.name} Trails` : this.state.titleString;
            this.setState({ filters: newFilterState, titleString, results: null, loading: false }, this.replaceHistoryStateAndSearch);
          } else {
            this.updateCenterAndZoomWithObjGeoLoc(newFilterState, obj);
          }
        },
        // error
        () => {
          this.updateCenterAndZoomWithObjGeoLoc(newFilterState, obj);
        }
      );
    } else {
      this.updateCenterAndZoomWithObjGeoLoc(newFilterState, obj);
    }
  },
  updateCenterAndZoomWithObjGeoLoc(newFilterState, obj) {
    // Zoom
    const locationType = obj.objectID.split('-')[0];
    const zoom = this.refs.searchMap.getMaxZoom(locationType);
    // If we have a geolocation for the object use it
    // Otherwise use the calculated name to geocode the location
    if (obj._geoloc != null) {
      this.refs.searchMap.setMapCenterAndZoom(obj._geoloc.lat, obj._geoloc.lng, zoom, { suppressBoundsCallback: true });
      const newBounds = this.refs.searchMap.getMapBounds();
      newFilterState = SearchFiltersUtil.setBoundsFilter(newFilterState, newBounds[0], newBounds[2], newBounds[1], newBounds[3]);
      const titleString = this.state.page === PageStrings.EXPLORE_ALL_PAGE ? `Map of ${obj.name} Trails` : this.state.titleString;
      this.setState({ filters: newFilterState, titleString }, this.replaceHistoryStateAndSearch);
    } else {
      const objName = obj.objectID.indexOf('city') >= 0 ? obj.name[1] : obj.name;
      $.ajax({
        url: `https://api.mapbox.com/v4/geocode/mapbox.places/${objName}.json?access_token=pk.eyJ1IjoiYWxsdHJhaWxzIiwiYSI6IkZGaGhGLWsifQ.Fl0FrDM8-xonRV6x2HFqcw`,
        success: data => {
          this.refs.searchMap.setMapCenterAndZoom(data.features[0].center[1], data.features[0].center[0], zoom, { suppressBoundsCallback: true });
          const newBounds = this.refs.searchMap.getMapBounds();
          newFilterState = SearchFiltersUtil.setBoundsFilter(newFilterState, newBounds[0], newBounds[2], newBounds[1], newBounds[3]);
          this.setState({ filters: newFilterState }, this.replaceHistoryStateAndSearch);
        }
      });
    }
  },
  resetPlaceFilterState(newFilterState, placesKey) {
    const placesFilter = newFilterState[placesKey];
    Object.keys(placesFilter).forEach(placeKey => {
      placesFilter[placeKey].selected = false;
    });
  },
  resetPlacesFilterState(newFilterState) {
    // remove places existing filters, regardless of type of object selected
    this.resetPlaceFilterState(newFilterState, 'areas');
    this.resetPlaceFilterState(newFilterState, 'cities');
    this.resetPlaceFilterState(newFilterState, 'states');
    this.resetPlaceFilterState(newFilterState, 'countries');
  }
};

export { SearchAppAlgoliaMixin };
